// @ts-check
/**
 * Module core/biblio-db
 *
 * Wraps IndexedDB, allowing the storage of references and aliases on the
 * client.
 *
 * It's a standalone module that can be imported into other modules.
 *
 */
import { idb } from "./import-maps.js";

export const name = "core/biblio-db";

/**
 * @typedef {keyof BiblioDb} AllowedType
 * @type {Set<AllowedType>}
 */
const ALLOWED_TYPES = new Set(["alias", "reference"]);
/* Database initialization tracker */
const readyPromise = openIdb();

/**
 * @typedef {object} BiblioDb
 *
 * @property {object} alias Object store for alias objects
 * @property {string} alias.key
 * @property {object} alias.value
 * @property {object} alias.indexes
 * @property {string} alias.aliasOf
 *
 * @property {object} reference Object store for reference objects
 * @property {string} reference.key
 * @property {object} reference.value
 *
 * @returns {Promise<import("idb").IDBPDatabase<BiblioDb>>}
 */
async function openIdb() {
  /** @type {import("idb").IDBPDatabase<BiblioDb>} */
  const db = await idb.openDB("respec-biblio2", 12, {
    upgrade(db) {
      Array.from(db.objectStoreNames).map(storeName =>
        db.deleteObjectStore(storeName)
      );
      const store = db.createObjectStore("alias", { keyPath: "id" });
      store.createIndex("aliasOf", "aliasOf", { unique: false });
      db.createObjectStore("reference", { keyPath: "id" });
    },
  });
  // Clean the database of expired biblio entries.
  const now = Date.now();
  for (const storeName of [...ALLOWED_TYPES]) {
    const tx = db.transaction(storeName, "readwrite");
    const range = IDBKeyRange.lowerBound(now);
    const sweep = async () => {
      let result = await tx.store.openCursor(range);
      while (result?.value) {
        /** @type {StoredBiblioEntry} */
        const entry = result.value;
        if (entry.expires === undefined || entry.expires < now) {
          await tx.store.delete(entry.id);
        }
        result = await result.continue();
      }
    };
    // Await the work and the commit together. If a request rejects, IndexedDB aborts the
    // transaction and idb rejects tx.done as well; awaiting them in sequence means this line
    // is never reached and that second rejection has no handler.
    await Promise.all([sweep(), tx.done]);
  }

  return db;
}

export const biblioDB = {
  get ready() {
    return readyPromise;
  },
  /**
   * Finds either a reference or an alias.
   * If it's an alias, it resolves it.
   *
   * @param {String} id The reference or alias to look for.
   * @return {Promise<BiblioData?>} The reference or null.
   */
  async find(id) {
    if (await this.isAlias(id)) {
      id = (await this.resolveAlias(id)) ?? id;
    }
    return await this.get("reference", id);
  },
  /**
   * Checks if the database has an id for a given type.
   *
   * @param {AllowedType} type One of the ALLOWED_TYPES.
   * @param {String} id The reference to find.
   * @return {Promise<Boolean>} True if it has it, false otherwise.
   */
  async has(type, id) {
    if (!ALLOWED_TYPES.has(type)) {
      throw new TypeError(`Invalid type: ${type}`);
    }
    if (!id) {
      throw new TypeError("id is required");
    }
    const db = await this.ready;
    const objectStore = db.transaction(type, "readonly").store;
    const range = IDBKeyRange.only(id);
    const result = await objectStore.openCursor(range);
    return !!result;
  },
  /**
   * Checks if a given id is an alias.
   *
   * @param {String} id The reference to check.
   * @return {Promise<Boolean>} Resolves with true if found.
   */
  async isAlias(id) {
    return await this.has("alias", id);
  },
  /**
   * Resolves an alias to its corresponding reference id.
   *
   * @param {String} id The id of the alias to look up.
   * @return {Promise<String | null>} The id of the resolved reference.
   */
  async resolveAlias(id) {
    if (!id) {
      throw new TypeError("id is required");
    }
    const db = await this.ready;

    const objectStore = db.transaction("alias", "readonly").store;
    const range = IDBKeyRange.only(id);
    const result = await objectStore.openCursor(range);
    return result ? result.value.aliasOf : null;
  },
  /**
   * Get a reference or alias out of the database.
   *
   * @param {AllowedType} type The type as per ALLOWED_TYPES.
   * @param {string} id The id for what to look up.
   * @return {Promise<BiblioData?>} Resolves with the retrieved object, or null.
   */
  async get(type, id) {
    if (!ALLOWED_TYPES.has(type)) {
      throw new TypeError(`Invalid type: ${type}`);
    }
    if (!id) {
      throw new TypeError("id is required");
    }
    const db = await this.ready;
    const objectStore = db.transaction(type, "readonly").store;
    const range = IDBKeyRange.only(id);
    const result = await objectStore.openCursor(range);
    return result ? result.value : null;
  },
  /**
   * Adds references and aliases to database. This is usually the data from
   * Specref's output (parsed JSON).
   *
   * @param {BibliographyMap} data An object that contains references and aliases.
   * @param {number} expires The date/time when the data expires.
   */
  async addAll(data, expires) {
    if (!data) {
      return;
    }
    /** @type {{ alias: StoredBiblioEntry[], reference: StoredBiblioEntry[] }} */
    const aliasesAndRefs = { alias: [], reference: [] };
    for (const id of Object.keys(data)) {
      /** @type {StoredBiblioEntry} */
      const obj = { id, ...data[id], expires };
      if (obj.aliasOf) {
        aliasesAndRefs.alias.push(obj);
      } else {
        aliasesAndRefs.reference.push(obj);
      }
    }
    const promisesToAdd = [...ALLOWED_TYPES].flatMap(type => {
      return aliasesAndRefs[type].map(details => this.add(type, details));
    });
    await Promise.all(promisesToAdd);
  },
  /**
   * Adds a reference or alias to the database.
   *
   * @param {AllowedType} type The type as per ALLOWED_TYPES.
   * @param {StoredBiblioEntry} details The object to store.
   */
  async add(type, details) {
    if (!ALLOWED_TYPES.has(type)) {
      throw new TypeError(`Invalid type: ${type}`);
    }
    if (typeof details !== "object") {
      throw new TypeError("details should be an object");
    }
    if (type === "alias" && !details.hasOwnProperty("aliasOf")) {
      throw new TypeError("Invalid alias object.");
    }
    const db = await this.ready;
    let isInDB = await this.has(type, details.id);
    // update or add, depending of already having it in db
    // or if it's expired
    if (isInDB) {
      const entry = await this.get(type, details.id);
      if (entry && entry.expires !== undefined && entry.expires < Date.now()) {
        const tx = db.transaction(type, "readwrite");
        await Promise.all([tx.store.delete(details.id), tx.done]);
        isInDB = false;
      }
    }
    const tx = db.transaction(type, "readwrite");
    // Await commit as well as the request: a later read in its own transaction may otherwise
    // not see this write, and awaiting them together keeps an aborted transaction's rejection
    // handled rather than unhandled.
    const [result] = await Promise.all([
      isInDB ? tx.store.put(details) : tx.store.add(details),
      tx.done,
    ]);
    return result;
  },
  /**
   * Closes the underlying database.
   *
   * @return {Promise<void>} Resolves after database closes.
   */
  async close() {
    const db = await this.ready;
    db.close();
  },

  /**
   * Clears the underlying database
   */
  async clear() {
    const db = await this.ready;
    const storeNames = [...ALLOWED_TYPES];
    const stores = db.transaction(storeNames, "readwrite");
    const clearStorePromises = storeNames.map(name => {
      return stores.objectStore(name).clear();
    });
    await Promise.all([...clearStorePromises, stores.done]);
  },
};
